/**
 * Drivers API client · T-S1-A W5
 * 真契约源：apps/api-server/src/api_server/routes/drivers.py
 */
import { api } from "./api";
import type {
  DriverAuditLog,
  DriverCreatePayload,
  DriverListQuery,
  DriverQrToken,
  DriverQrTokenCreatePayload,
  DriverQrTokenRevokePayload,
  DriverRead,
  DriverRecentOrder,
  DriverReviewSummary,
  DriverScheduleResponse,
  DriverScheduleDay,
  ScheduleDayOverridePayload,
  DriverStats,
  DriverStatusUpdatePayload,
  DriverUpdatePayload,
} from "./types/drivers";
import type { SmsOptOutStatus, SmsOptOutTogglePayload } from "./types/smsOptOut";

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ─── Driver CRUD ──────────────────────────────────────────────────────────────

export async function listDrivers(
  q: DriverListQuery = {},
): Promise<DriverRead[]> {
  const qs = buildQuery({
    status: q.status,
    q: q.q,
    limit: q.limit,
    offset: q.offset,
  });
  return api<DriverRead[]>(`/api/v1/drivers${qs}`);
}

export async function getDriverStats(): Promise<DriverStats> {
  return api<DriverStats>("/api/v1/drivers/_stats");
}

export async function getDriver(id: string): Promise<DriverRead> {
  return api<DriverRead>(`/api/v1/drivers/${id}`);
}

export async function createDriver(
  payload: DriverCreatePayload,
): Promise<DriverRead> {
  return api<DriverRead>("/api/v1/drivers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateDriver(
  id: string,
  payload: DriverUpdatePayload,
): Promise<DriverRead> {
  return api<DriverRead>(`/api/v1/drivers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateDriverStatus(
  id: string,
  payload: DriverStatusUpdatePayload,
): Promise<DriverRead> {
  return api<DriverRead>(`/api/v1/drivers/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteDriver(id: string): Promise<void> {
  await api<void>(`/api/v1/drivers/${id}`, { method: "DELETE" });
}

/** CONSENT-1 · 短信退订/撤销退订（按司导手机号·sms_opt_outs 表） */
export async function toggleDriverSmsOptOut(
  id: string,
  payload: SmsOptOutTogglePayload,
): Promise<SmsOptOutStatus> {
  return api<SmsOptOutStatus>(`/api/v1/drivers/${id}/sms-opt-out`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// ─── Schedule ─────────────────────────────────────────────────────────────────

export async function getDriverSchedule(
  id: string,
  monthStart?: string,
): Promise<DriverScheduleResponse> {
  const qs = buildQuery({ month_start: monthStart });
  return api<DriverScheduleResponse>(`/api/v1/drivers/${id}/schedule${qs}`);
}

/** PUT /api/v1/drivers/{id}/schedule/{day} — OP 按天排班（available=清除覆盖）*/
export async function setDriverScheduleDay(
  id: string,
  day: string,
  payload: ScheduleDayOverridePayload,
): Promise<DriverScheduleDay> {
  return api<DriverScheduleDay>(`/api/v1/drivers/${id}/schedule/${day}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

// ─── 详情 sections ⑤ 历史派单 / ⑥ 客户评价 / ⑦ 操作日志 ───────────────────────

export async function getDriverRecentOrders(
  id: string,
  days = 30,
): Promise<DriverRecentOrder[]> {
  return api<DriverRecentOrder[]>(
    `/api/v1/drivers/${id}/recent-orders?days=${days}`,
  );
}

export async function getDriverReviews(
  id: string,
  months = 6,
): Promise<DriverReviewSummary> {
  return api<DriverReviewSummary>(
    `/api/v1/drivers/${id}/reviews?months=${months}`,
  );
}

export async function getDriverAuditLogs(
  id: string,
  limit = 50,
): Promise<DriverAuditLog[]> {
  return api<DriverAuditLog[]>(
    `/api/v1/drivers/${id}/audit-logs?limit=${limit}`,
  );
}

// ─── QR Tokens ────────────────────────────────────────────────────────────────

export async function listDriverQrTokens(
  driverId: string,
  includeRevoked = false,
): Promise<DriverQrToken[]> {
  const qs = includeRevoked ? "?include_revoked=true" : "";
  return api<DriverQrToken[]>(`/api/v1/drivers/${driverId}/qr-tokens${qs}`);
}

export async function createDriverQrToken(
  driverId: string,
  payload: DriverQrTokenCreatePayload = {},
): Promise<DriverQrToken> {
  return api<DriverQrToken>(`/api/v1/drivers/${driverId}/qr-tokens`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function revokeDriverQrToken(
  driverId: string,
  tokenId: string,
  payload: DriverQrTokenRevokePayload = {},
): Promise<DriverQrToken> {
  return api<DriverQrToken>(
    `/api/v1/drivers/${driverId}/qr-tokens/${tokenId}`,
    {
      method: "DELETE",
      body: JSON.stringify(payload),
    },
  );
}

// ─── R-18 司导请假审批（OP 经理）──────────────────────────────
export interface DriverLeaveRequest {
  id: string;
  driver_user_id: string;
  driver_name: string | null;
  leave_type: string;
  starts_on: string;
  ends_on: string;
  reason: string | null;
  status: string;
  decision_notes: string | null;
  approved_at: string | null;
  created_at: string;
}

export async function listLeaveRequests(status?: string): Promise<DriverLeaveRequest[]> {
  const qs = status ? `?status=${status}` : "";
  return api<DriverLeaveRequest[]>(`/api/v1/drivers/leave-requests${qs}`);
}

export async function approveLeaveRequest(id: string, notes?: string): Promise<DriverLeaveRequest> {
  return api<DriverLeaveRequest>(`/api/v1/drivers/leave-requests/${id}/approve`, {
    method: "PATCH",
    body: JSON.stringify({ notes: notes ?? null }),
  });
}

export async function rejectLeaveRequest(id: string, notes: string): Promise<DriverLeaveRequest> {
  return api<DriverLeaveRequest>(`/api/v1/drivers/leave-requests/${id}/reject`, {
    method: "PATCH",
    body: JSON.stringify({ notes }),
  });
}

// ─── A5R 司导改派审批（OP 经理·批准记爽约/驳回不计）──────────────
export interface DriverReassignRequest {
  id: string;
  dispatch_order_id: string;
  dispatch_number: string | null;
  driver_user_id: string;
  driver_name: string | null;
  reason: string;
  status: string;
  counts_as_no_show: boolean;
  decision_note: string | null;
  requested_at: string;
  decided_at: string | null;
}

export async function listReassignRequests(status?: string): Promise<DriverReassignRequest[]> {
  const qs = status ? `?status=${status}` : "";
  return api<DriverReassignRequest[]>(`/api/v1/drivers/reassign-requests${qs}`);
}

export async function approveReassignRequest(id: string, notes?: string): Promise<DriverReassignRequest> {
  return api<DriverReassignRequest>(`/api/v1/drivers/reassign-requests/${id}/approve`, {
    method: "PATCH",
    body: JSON.stringify({ notes: notes ?? null }),
  });
}

export async function rejectReassignRequest(id: string, notes: string): Promise<DriverReassignRequest> {
  return api<DriverReassignRequest>(`/api/v1/drivers/reassign-requests/${id}/reject`, {
    method: "PATCH",
    body: JSON.stringify({ notes }),
  });
}

// ─── A5②③ 司导综合分（7 维真实指标 + 0-100 综合分）契约源 services/driver_metrics.py ──
export interface DriverComprehensiveScore {
  driver_id: string;
  window_days: number;
  composite_score: number; // 0-100
  dimensions: {
    rating: { avg_rating: number | null; review_count: number; five_star_rate: number; complaint_rate: number; has_data: boolean };
    on_time: { on_time_count: number; checkpoint_count: number; on_time_rate: number; has_data: boolean };
    reliability: { no_show_count: number; total_tours: number; no_show_rate: number; reliability: number; has_data: boolean };
    dispatchable: { tours_this_month: number; available_days: number; leave_days: number; dispatchable_rate: number; has_data: boolean };
    five_star: { five_star_rate: number; has_data: boolean };
    complaint: { complaint_rate: number; has_data: boolean };
    self_pay: { conversion_rate: number | null; per_pax_cents: number | null; has_data: boolean; pending: boolean };
  };
  weights: Record<string, number>;
}

export async function getComprehensiveScore(driverId: string, windowDays = 90): Promise<DriverComprehensiveScore> {
  return api<DriverComprehensiveScore>(`/api/v1/drivers/${driverId}/comprehensive-score?window_days=${windowDays}`);
}
